import React from "react";
import { CalendarDays, Clock3, Mail, Tag, AlertTriangle, Image as ImageIcon, ClipboardCheck } from "lucide-react";
import { DD_MM_YYYY, timeFormat } from "src/helpers";
import { WarehouseCheckInFlaggedIssue } from "src/types";
import { FileDownloadButton, NoteDisplay } from "src/components";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface WarehouseCheckInCheckInDetailsSectionProps {
  timestamp: string;
  asn?: string;
  checkInUserEmail?: string;
  flaggedIssues?: WarehouseCheckInFlaggedIssue[];
  hasImages?: boolean;
  downloadCheckInImages?: () => void;
  isFileDownloading?: boolean;
  initiallyOpen?: boolean;
  withCollapsible?: boolean;
}

const WarehouseCheckInCheckInDetailsSection: React.FC<WarehouseCheckInCheckInDetailsSectionProps> = ({
  timestamp,
  asn,
  checkInUserEmail,
  flaggedIssues,
  hasImages,
  downloadCheckInImages,
  isFileDownloading,
  initiallyOpen = true,
  withCollapsible = true
}) => {
  const parsedTimestamp = timestamp ? new Date(timestamp) : undefined;
  const formattedDate = parsedTimestamp ? DD_MM_YYYY.formatDate(parsedTimestamp) : undefined;
  const formattedTime = parsedTimestamp ? timeFormat(parsedTimestamp) : undefined;
  const hasFlaggedIssues = !!flaggedIssues && flaggedIssues.length > 0;

  const content = (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {asn && (
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Tag className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">ASN</span>
            </div>
            <p className="text-sm font-medium text-secondary-900">{asn}</p>
          </div>
        )}

        {checkInUserEmail && (
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Mail className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">
                Check-in Recorded By
              </span>
            </div>
            <p className="text-sm font-medium text-secondary-900">{checkInUserEmail}</p>
          </div>
        )}

        {formattedDate && (
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <CalendarDays className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">Check-in Date</span>
            </div>
            <p className="text-sm font-medium text-secondary-900">{formattedDate}</p>
          </div>
        )}

        {formattedTime && (
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Clock3 className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">Check-in Time</span>
            </div>
            <p className="text-sm font-medium text-secondary-900">{formattedTime}</p>
          </div>
        )}
      </div>

      {hasImages && downloadCheckInImages && (
        <div className="flex flex-col gap-4 rounded-lg border border-secondary-200 bg-secondary-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-secondary-100 p-2 text-secondary-700">
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-secondary-800">Check-in Images</p>
              <p className="text-xs text-secondary-600">Download the photo set captured during the warehouse intake.</p>
            </div>
          </div>

          <FileDownloadButton
            handleDownload={downloadCheckInImages}
            isDisabled={isFileDownloading}
            buttonText={isFileDownloading ? "Preparing..." : "Download"}
          />
        </div>
      )}

      {hasFlaggedIssues && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-error-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-error-800 mb-3">Flagged Issues</p>
              <div className="space-y-2">
                {flaggedIssues?.map((flaggedIssue, index) => (
                  <div key={index} className="rounded-md bg-white/70 p-3">
                    <NoteDisplay note={{ body: flaggedIssue, date: "" }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!withCollapsible) {
    return (
      <section className="rounded-2xl border border-secondary-200 bg-white shadow-soft">
        <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
          <ClipboardCheck className="h-5 w-5 text-primary-600" />
          <h3 className="text-base font-semibold text-secondary-900">Check-in Details</h3>
        </div>
        {content}
      </section>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={initiallyOpen}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <ClipboardCheck className="w-5 h-5 mr-2 text-primary-600" />
            Check-in Details
          </h3>
        }
      >
        {content}
      </CollapsibleSection>
    </div>
  );
};

export { WarehouseCheckInCheckInDetailsSection };
